import React from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Grid,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Container,
  Tooltip
} from "@material-ui/core";
import Warning from "../../utils/Warning";
import Error from "../../utils/Error";
import { flatten, orderBy } from "lodash";
import ProgressButton from "../../utils/ProgressButton";
import PropTypes from "prop-types";
import SaveIcon from "@material-ui/icons/Save";
import { useQuery, useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import Filter from "../../utils/Filter";
import clsx from "clsx";

export const GET_CUTOMERS_MODULES_RIGHTS = gql`
  query($stage: String!, $search: String) {
    AWACustomersModulesRightsByStage(stage: $stage, search: $search) {
      customers {
        rights {
          module_id
          access
          env
          customer_id
          name
          url
        }
        customer_id
        stage
        url
        name
        trigram
        isActive
        createdAt
        updatedAt
        plan {
          id
          name
        }
      }
      stage
      service {
        url
      }
    }
  }
`;

const GET_AWA_CLIENT_MODULES = gql`
  {
    AWAClientModules {
      name
    }
  }
`;

const GET_PLANS = gql`
  {
    Plans {
      id
      name
    }
  }
`;

const UPDATE_CUSTOMER_MODULES_RIGHTS = gql`
  mutation updateCustomerModuleRight($rights: [AwaCustomerModuleRightInput]!) {
    updateCustomerModuleRight(rights: $rights)
  }
`;

const UPDATE_CUSTOMER_PLAN = gql`
  mutation updateCustomerPlanByStage(
    $customerId: String!
    $planId: String!
    $stage: String!
  ) {
    updateCustomerPlanByStage(
      customerId: $customerId
      planId: $planId
      stage: $stage
    )
  }
`;

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column"
  },
  container: {
    display: "block",
    margin: 15
  },
  select: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  info: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.error.contrastText
  },
  switchBase: {
    "&$checked": {
      color: theme.palette.success.main
    },
    "&$checked + $track": {
      backgroundColor: theme.palette.success.dark
    }
  },
  thumb: {
    "&:hover": {
      backgroundColor: "#FFF"
    }
  },
  checked: {},
  track: {},
  search: {
    width: "100%"
  },
  sizeSmall: {
    padding: "0px"
  },
  moduleRoot: {
    // transform: "rotate(315deg)",
    fontSize: "10px"
  },
  error: {
    padding: theme.spacing(2),
    background: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    marginBottom: theme.spacing(2)
  },
  changed: {
    background: "#ffccbc"
  },
  saveIcon: {
    marginLeft: theme.spacing(1)
  },
  saveIconButton: {
    padding: "0px"
  },
  saveCircularButton: {
    width: "20px !important",
    height: "20px !important"
  },
  formControlSwitch: {
    width: "100%",
    margin: "0px",
    justifyContent: "center"
  },
  title: {
    flexGrow: "1"
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    marginTop: theme.spacing(3)
  },
  stickyCellDefault: {
    position: "sticky",
    backgroundColor: "#ffffff"
  },
  stickyCellY: {
    position: "sticky",
    backgroundColor: "#ffffff",
    top: "0",
    zIndex: "99"
  },
  stickyCellXY: {
    position: "sticky",
    backgroundColor: "#ffffff",
    top: "0",
    zIndex: "101 !important"
  },
  secondStickyCell: {
    left: "82px",
    zIndex: "100"
  },
  firstStickyCell: {
    left: "0px",
    zIndex: "100"
  },
  warningHeader: {
    flexWrap: "nowrap",
    cursor: "pointer"
  },
  warningConfig: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    padding: theme.spacing(2)
  },
  tabContainer: {
    paddingTop: "0px !important",
    paddingLeft: "0px !important",
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    width: "100%",
    height: "60vh",
    overflowX: "scroll",
    overflowY: "scroll"
  }
}));

ModulesRights.propTypes = {};

export default function ModulesRights() {
  const classes = useStyles();
  const [
    filteredModulesRightsByStage,
    setFilteredModulesRightsByStage
  ] = React.useState([]);
  const [modules, setModules] = React.useState([]);
  const [initModulesRights, setInitModulesRights] = React.useState({});
  const [changedModulesRights, setChangedModulesRights] = React.useState({});
  const [changedAllPlanRights, setChangedAllPlanRights] = React.useState({});
  const [filter, setFilter] = React.useState("");
  const [refetching, setRefetching] = React.useState(false);
  const [showADS, setShowADS] = React.useState(false);
  const [processHandle, setProcessHandle] = React.useState(true);
  const [variables, setVariables] = React.useState({
    stage: filter
  });
  const [selectedPlans, setSelectedPlans] = React.useState({});

  const awaClientModules = useQuery(GET_AWA_CLIENT_MODULES, {
    fetchPolicy: "cache-first"
  });

  const { loading, error, data, refetch } = useQuery(
    GET_CUTOMERS_MODULES_RIGHTS,
    {
      variables,
      fetchPolicy: "cache-and-network",
      skip: !variables.stage || variables.stage.length < 1
    }
  );

  const plans = useQuery(GET_PLANS, {
    fetchPolicy: "cache-first"
  });

  const [
    updateCustomerModuleRight,
    updateCustomerModuleRightStatus
  ] = useMutation(UPDATE_CUSTOMER_MODULES_RIGHTS);

  const [updateCustomerPlanByStage] = useMutation(UPDATE_CUSTOMER_PLAN);

  React.useEffect(() => {
    const hasAp = [
      "AIRWORK",
      "ELOGBOOK",
      "EQUAL",
      "MTV",
      "CORE",
      "WORKSHOP",
      "FINANCIAL",
      "FLIGHTOPS",
      "COSTMONITORING"
    ];
    if (
      awaClientModules &&
      awaClientModules.data &&
      awaClientModules.data.AWAClientModules
    ) {
      setModules(
        awaClientModules.data.AWAClientModules.map(module => ({
          name: module.name,
          hasAp: hasAp.includes(module.name)
        })).sort(({ name: aName }, { name: bName }) =>
          aName.localeCompare(bName)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaClientModules.data]);

  React.useEffect(() => {
    if (data && data.AWACustomersModulesRightsByStage) {
      const allModuleRights = data.AWACustomersModulesRightsByStage.reduce(
        (accS, byStage) => ({
          ...accS,
          ...byStage.customers.reduce(
            (accC, customer) => ({
              ...accC,
              ...customer.rights.reduce(
                (accR, right) => ({
                  ...accR,
                  [formId(right)]: right
                }),
                {}
              )
            }),
            {}
          )
        }),
        {}
      );
      setInitModulesRights(allModuleRights);
      setChangedModulesRights({});
      // Après un refetch, réinitialiser selectedPlans pour utiliser les plans actualisés
      setSelectedPlans({});
      setRefetching(false);
      filterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  React.useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showADS]);

  function filterData() {
    if (data && data.AWACustomersModulesRightsByStage) {
      const allCustomersByStage = orderBy(
        flatten(
          (data.AWACustomersModulesRightsByStage || []).map(byStage =>
            byStage.customers.filter(customer =>
              showADS ? true : !(customer.trigram === "ADS")
            )
          )
        ),
        "trigram"
      );
      const filteredFilter = allCustomersByStage.filter(
        customer =>
          customer.stage.toLowerCase().includes(filter.toLowerCase()) ||
          customer.trigram.toLowerCase().includes(filter.toLowerCase())
      );
      setFilteredModulesRightsByStage(filteredFilter);
    }
  }

  function formId(right) {
    return `${right.customer_id}__${right.env}__${right.module_id}`;
  }

  return (
    <Container
      maxWidth="lg"
      onKeyPress={e =>
        e && e.which === 13
          ? setVariables({
              stage: filter
            })
          : null
      }
    >
      {updateCustomerModuleRightStatus &&
        updateCustomerModuleRightStatus.error && (
          <Paper className={classes.error}>
            {updateCustomerModuleRightStatus.error.message}
          </Paper>
        )}
      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          <Grid item container>
            <Typography component="h1" variant="h4" className={classes.title}>
              Manage customer's modules rights
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showADS}
                  onChange={e => {
                    setShowADS(!showADS);
                  }}
                  value={`showADS`}
                  color="primary"
                  inputProps={{ "aria-label": "primary checkbox" }}
                />
              }
              label={`Show ADS customer`}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={processHandle}
                  onChange={e => {
                    setProcessHandle(!processHandle);
                  }}
                  value={`processHandle`}
                  color="primary"
                  inputProps={{ "aria-label": "primary checkbox" }}
                />
              }
              label={`Process action on module activation`}
            />
            <Filter
              name={`filter-stage`}
              className={classes.search}
              placeholder={
                "Stage à rechercher ex: PROD--14-11, ADMIN--14-8, BETATESTELB, DEV..."
              }
              value={filter}
              onChange={e => setFilter(e)}
            />
            <Grid item xl={12} className={classes.buttons}>
              <ProgressButton
                label={"Search"}
                action={() =>
                  setVariables({
                    stage: filter
                  })
                }
                busy={loading}
              />
            </Grid>
          </Grid>
          <Error
            className={classes.warningConfig}
            showIcon={true}
            messages={
              <Typography classes={{ root: classes.moduleRoot }}>
                DON'T FORGET TO ACTIVATE/DEACTIVATE CORRESPONDING SERVICE IN
                AIRPACK CONFIG FILE FOR MODULES MARKED WITH A *
              </Typography>
            }
          />
          <Grid item container className={classes.tabContainer}>
            {awaClientModules.loading && (
              <Grid container justify="center">
                <Grid item>
                  <CircularProgress data-testid="loading" />
                </Grid>
              </Grid>
            )}
            {!awaClientModules.loading && !awaClientModules.error && (
              <Table size="small">
                <TableHead>
                  <TableRow style={{ zIndex: "50" }}>
                    <TableCell
                      key={`th-trigramn`}
                      align={"center"}
                      className={clsx(
                        classes.firstStickyCell,
                        classes.stickyCellXY
                      )}
                    >
                      Trigram
                    </TableCell>
                    <TableCell
                      key={`th-stage`}
                      align={"center"}
                      className={clsx(
                        classes.secondStickyCell,
                        classes.stickyCellXY
                      )}
                    >
                      Stage
                    </TableCell>
                    <TableCell
                      className={clsx(classes.stickyCellXY)}
                      key={`th-stage`}
                      align={"center"}
                    >
                      PREMIUM PLAN
                    </TableCell>

                    {modules.map(({ name, hasAp }) => (
                      <TableCell
                        key={`th-module-${name}`}
                        align={"center"}
                        className={classes.stickyCellY}
                      >
                        {name === "AIRWORK" && processHandle ? (
                          <Tooltip title="Enabling this module will enable 'MRO - general' in Airpack ( it needs a started deployment to work )">
                            <div>
                              <Warning
                                className={classes.warningHeader}
                                showIcon={true}
                                messages={
                                  <Typography
                                    classes={{ root: classes.moduleRoot }}
                                  >
                                    {name}
                                    {hasAp && "*"}
                                  </Typography>
                                }
                              />
                            </div>
                          </Tooltip>
                        ) : (
                          <Typography classes={{ root: classes.moduleRoot }}>
                            {name}
                            {hasAp && "*"}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading &&
                    !error &&
                    !!modules.length &&
                    filteredModulesRightsByStage &&
                    filteredModulesRightsByStage.map(
                      ({ customer_id, trigram, rights, stage, plan }) => (
                        <TableRow key={`row-${customer_id}-${stage}`}>
                          <SaveCell
                            customerId={customer_id}
                            stage={stage}
                            trigram={trigram}
                            refetching={refetching}
                            setRefetching={setRefetching}
                            updateStatus={updateCustomerModuleRightStatus}
                            changedRights={changedModulesRights}
                            updateRight={updateCustomerModuleRight}
                            changedAllPlanRights={changedAllPlanRights}
                            rights={rights}
                            plans={plans}
                            updateCustomerPlanByStage={
                              updateCustomerPlanByStage
                            }
                            currentPlan={plan}
                            selectedPlans={selectedPlans}
                            setChangedAllPlanRights={setChangedAllPlanRights}
                            setChangedModulesRights={setChangedModulesRights}
                            setSelectedPlans={setSelectedPlans}
                            refetch={refetch}
                          />
                          <TableCell
                            key={`cell-${customer_id}-${stage}-stage`}
                            align={"center"}
                            className={clsx(
                              classes.stickyCellDefault,
                              classes.secondStickyCell,
                              classes.sizeSmall
                            )}
                          >
                            {stage}
                          </TableCell>
                          <PlanCell
                            plan={plan}
                            customerId={customer_id}
                            stage={stage}
                            changedAllPlanRights={changedAllPlanRights}
                            setChangedAllPlanRights={setChangedAllPlanRights}
                            initRights={initModulesRights}
                            rights={rights}
                            changeRight={setChangedModulesRights}
                            plans={plans}
                            selectedPlans={selectedPlans}
                            setSelectedPlans={setSelectedPlans}
                          />

                          {modules.map(({ name }) => (
                            <RightCell
                              key={`cell-${customer_id}-${stage}-${name}`}
                              rights={rights}
                              module={name}
                              formId={formId}
                              trigram={trigram}
                              customerId={customer_id}
                              stage={stage}
                              updateRight={updateCustomerModuleRight}
                              changeRight={setChangedModulesRights}
                              initRights={initModulesRights}
                              changedRights={changedModulesRights}
                              processHandle={processHandle}
                              changedAllPlanRights={changedAllPlanRights}
                              setChangedAllPlanRights={setChangedAllPlanRights}
                            />
                          ))}
                        </TableRow>
                      )
                    )}
                </TableBody>
              </Table>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}

const SaveCell = React.memo(
  ({
    trigram,
    customerId,
    stage,
    changedRights,
    updateRight,
    updateStatus,
    setRefetching,
    refetching,
    changedAllPlanRights,
    rights,
    plans,
    updateCustomerPlanByStage,
    currentPlan,
    selectedPlans,
    setChangedAllPlanRights,
    setChangedModulesRights,
    setSelectedPlans,
    refetch
  }) => {
    const classes = useStyles();
    const id = `${customerId}__${stage}`;
    const regex = new RegExp(`^${id}__`, "i");

    // Check for individual changes
    const rowChanged = Object.keys(changedRights).filter(right =>
      regex.test(right)
    );

    // Get the client-specific plan rights
    const clientPlanRights = changedAllPlanRights[customerId] || [];

    // Check if there are plan changes for this customer
    const hasPlanChanges = clientPlanRights.length > 0;

    // Get the selected plan for this customer, or use the current plan if not changed
    const selectedPlan = selectedPlans[customerId] || currentPlan;
    const planChanged = selectedPlan.id !== currentPlan.id;

    const busy =
      (updateStatus && updateStatus.loading === true) || refetching === true;

    const handleSave = async () => {
      if (!busy) {
        setRefetching(true);

        try {
          if (hasPlanChanges) {
            // Update module rights
            const planChanges = clientPlanRights.map(right => ({
              access: right.access,
              customer: trigram,
              customer_id: customerId,
              module_id: right.module_id,
              name: right.name,
              processHandle: true,
              stage: stage,
              url: right.url
            }));

            await updateRight({
              variables: {
                rights: planChanges
              },
              refetchQueries: [
                {
                  query: GET_CUTOMERS_MODULES_RIGHTS,
                  variables: {
                    stage: stage
                  }
                }
              ],
              awaitRefetchQueries: true
            });

            // Update plan if it has changed
            if (planChanged && plans && plans.data) {
              await updateCustomerPlanByStage({
                variables: {
                  customerId,
                  planId: selectedPlan.id,
                  stage
                },
                refetchQueries: [
                  {
                    query: GET_CUTOMERS_MODULES_RIGHTS,
                    variables: {
                      stage: stage
                    }
                  }
                ],
                awaitRefetchQueries: true
              });
            }
          } else if (rowChanged.length > 0) {
            // If there are only individual changes, just update those
            await updateRight({
              variables: {
                rights: Object.values(changedRights)
              },
              refetchQueries: [
                {
                  query: GET_CUTOMERS_MODULES_RIGHTS,
                  variables: {
                    stage: stage
                  }
                }
              ],
              awaitRefetchQueries: true
            });
          }

          // Réinitialiser tous les états après la sauvegarde
          const newChangedRights = { ...changedRights };
          Object.keys(newChangedRights).forEach(key => {
            if (key.startsWith(`${customerId}__${stage}`)) {
              delete newChangedRights[key];
            }
          });
          setChangedModulesRights(newChangedRights);

          // Supprimer les droits de plan modifiés pour ce client
          const newPlanRights = { ...changedAllPlanRights };
          delete newPlanRights[customerId];
          setChangedAllPlanRights(newPlanRights);

          // Réinitialiser le plan sélectionné pour ce client
          const newSelectedPlans = { ...selectedPlans };
          delete newSelectedPlans[customerId];
          setSelectedPlans(newSelectedPlans);
        } catch (error) {
          console.error("Error saving changes:", error);
        } finally {
          setRefetching(false);
        }
      }
    };

    return (
      <TableCell
        key={`cell-${id}-trigram`}
        align={"center"}
        className={clsx(
          classes.stickyCellDefault,
          classes.firstStickyCell,
          classes.sizeSmall
        )}
      >
        <Grid container justify="center" alignItems="center">
          <Grid item>{trigram}</Grid>
          <Grid item className={classes.saveIcon}>
            {rowChanged.length > 0 || hasPlanChanges ? (
              <IconButton
                classes={{ root: classes.saveIconButton }}
                onClick={handleSave}
              >
                {busy ? (
                  <CircularProgress
                    classes={{ root: classes.saveCircularButton }}
                  />
                ) : (
                  <SaveIcon />
                )}
              </IconButton>
            ) : null}
          </Grid>
        </Grid>
      </TableCell>
    );
  },
  (oldProps, newProps) => {
    return (
      oldProps.changedRights === newProps.changedRights &&
      oldProps.updateStatus === newProps.updateStatus &&
      oldProps.changedAllPlanRights === newProps.changedAllPlanRights &&
      oldProps.currentPlan === newProps.currentPlan &&
      oldProps.selectedPlans === newProps.selectedPlans
    );
  }
);

SaveCell.propTypes = {
  trigram: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  stage: PropTypes.string.isRequired,
  changedRights: PropTypes.object,
  updateRight: PropTypes.func.isRequired,
  updateStatus: PropTypes.object,
  setRefetching: PropTypes.func,
  refetching: PropTypes.bool,
  changedAllPlanRights: PropTypes.object,
  rights: PropTypes.array,
  plans: PropTypes.object,
  updateCustomerPlanByStage: PropTypes.func.isRequired,
  currentPlan: PropTypes.object.isRequired,
  selectedPlans: PropTypes.object.isRequired,
  setChangedAllPlanRights: PropTypes.func.isRequired,
  setChangedModulesRights: PropTypes.func.isRequired,
  setSelectedPlans: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired
};

const RightCell = React.memo(
  ({
    rights,
    module,
    formId,
    initRights,
    changedRights,
    changeRight,
    trigram,
    processHandle,
    changedAllPlanRights,
    setChangedAllPlanRights,
    customerId,
    stage
  }) => {
    const classes = useStyles();
    const findRight = rights.find(
      r => r.name.toLowerCase() === module.toLowerCase()
    );
    if (!findRight) {
      return (
        <TableCell
          align={"center"}
          classes={{ sizeSmall: classes.sizeSmall }}
        ></TableCell>
      );
    }
    const id = formId(findRight);
    const changed = Object.keys(changedRights).includes(id);
    const checked = initRights[id].access;
    const access = changed ? !checked : checked;

    // Only consider client-specific changedAllPlanRights if they exist
    const clientPlanRights = changedAllPlanRights[customerId] || [];
    const planRight =
      clientPlanRights.length > 0
        ? clientPlanRights.find(r => r.name === module)
        : null;
    const finalAccess = planRight ? planRight.access : access;

    return (
      <TableCell
        align={"center"}
        classes={{
          root: (changed || planRight) && classes.changed,
          sizeSmall: classes.sizeSmall
        }}
      >
        <FormControlLabel
          classes={{ root: classes.formControlSwitch }}
          control={
            <Switch
              classes={{
                switchBase: classes.switchBase,
                track: classes.track,
                checked: classes.checked,
                thumb: classes.thumb
              }}
              disableRipple
              checked={finalAccess}
              onChange={() => {
                const input = {
                  name: module,
                  access: !finalAccess,
                  customer: trigram,
                  stage: findRight.env,
                  customer_id: findRight.customer_id,
                  module_id: findRight.module_id,
                  url: findRight.url,
                  processHandle
                };

                // If there are plan changes for this client
                if (clientPlanRights.length > 0) {
                  const updatedClientRights = clientPlanRights.map(right =>
                    right.name === module ? input : right
                  );

                  setChangedAllPlanRights({
                    ...changedAllPlanRights,
                    [customerId]: updatedClientRights
                  });
                } else {
                  // Otherwise, handle individual changes as before
                  const newRights = Object.assign({}, changedRights);
                  if (changed) {
                    delete newRights[id];
                  } else {
                    newRights[id] = input;
                  }
                  changeRight(newRights);
                }
              }}
              value={`${id}`}
            />
          }
        />
      </TableCell>
    );
  },
  (oldProps, newProps) => {
    return (
      oldProps.changedRights === newProps.changedRights &&
      oldProps.processHandle === newProps.processHandle &&
      oldProps.changedAllPlanRights === newProps.changedAllPlanRights
    );
  }
);

RightCell.propTypes = {
  rights: PropTypes.array.isRequired,
  module: PropTypes.string.isRequired,
  formId: PropTypes.func.isRequired,
  initRights: PropTypes.object,
  changedRights: PropTypes.object,
  changeRight: PropTypes.func,
  trigram: PropTypes.string.isRequired,
  processHandle: PropTypes.bool,
  changedAllPlanRights: PropTypes.object,
  setChangedAllPlanRights: PropTypes.func,
  customerId: PropTypes.string.isRequired,
  stage: PropTypes.string.isRequired
};

const PlanCell = React.memo(
  ({
    rights,
    plan,
    setChangedAllPlanRights,
    changeRight,
    plans,
    customerId,
    selectedPlans,
    setSelectedPlans,
    changedAllPlanRights
  }) => {
    const classes = useStyles();

    // Get the selected plan from the shared state, or use the current plan if not yet selected
    const selectedPlan = selectedPlans[customerId] || plan;
    const changed = selectedPlan.id !== plan.id;

    const handlePlanChange = () => {
      if (!plans || !plans.data || !plans.data.Plans) return;

      // Déterminer si on passe à PREMIUM ou BASIC (l'inverse du plan actuel)
      const targetPlanName =
        selectedPlan.name === "PREMIUM" ? "BASIC" : "PREMIUM";

      // Trouver le plan cible dans la liste des plans
      const targetPlan = plans.data.Plans.find(p => p.name === targetPlanName);

      if (targetPlan) {
        // Mettre à jour le plan sélectionné
        setSelectedPlans({ ...selectedPlans, [customerId]: targetPlan });

        // Préparer les nouveaux droits en fonction du plan cible
        const newRights = [];
        for (const right of rights) {
          if (targetPlanName === "PREMIUM") {
            // BASIC -> PREMIUM (all rights true except FLIGHTOPS)
            newRights.push({
              ...right,
              access: right.name === "FLIGHTOPS" ? false : true
            });
          } else {
            // PREMIUM -> BASIC (all rights false except CORE)
            newRights.push({
              ...right,
              access: right.name === "CORE" ? true : false
            });
          }
        }

        // Reset individual changes when changing plan
        changeRight({});

        // Stocker les nouveaux droits par client
        setChangedAllPlanRights({
          ...changedAllPlanRights,
          [customerId]: newRights
        });
      }
    };

    return (
      <TableCell
        align={"center"}
        classes={{
          root: changed && classes.changed,
          sizeSmall: classes.sizeSmall
        }}
      >
        <FormControlLabel
          classes={{ root: classes.formControlSwitch }}
          control={
            <Switch
              classes={{
                switchBase: classes.switchBase,
                track: classes.track,
                checked: classes.checked,
                thumb: classes.thumb
              }}
              disableRipple
              checked={selectedPlan.name === "PREMIUM"}
              onChange={handlePlanChange}
              value={`toto`}
            />
          }
        />
      </TableCell>
    );
  },
  (oldProps, newProps) => {
    return (
      oldProps.changedRights === newProps.changedRights &&
      oldProps.processHandle === newProps.processHandle &&
      oldProps.selectedPlans === newProps.selectedPlans &&
      oldProps.changedAllPlanRights === newProps.changedAllPlanRights &&
      oldProps.plan.id === newProps.plan.id
    );
  }
);

PlanCell.propTypes = {
  rights: PropTypes.array.isRequired,
  plan: PropTypes.object.isRequired,
  initRights: PropTypes.object,
  setChangedAllPlanRights: PropTypes.func.isRequired,
  changeRight: PropTypes.func.isRequired,
  plans: PropTypes.object,
  customerId: PropTypes.string.isRequired,
  stage: PropTypes.string.isRequired,
  selectedPlans: PropTypes.object.isRequired,
  setSelectedPlans: PropTypes.func.isRequired,
  changedAllPlanRights: PropTypes.object
};
